/**
 * Frontend site config — values injected by Vite at build time via VITE_* env vars.
 * These replace all hardcoded brand references in the frontend.
 *
 * SITE_ID has no fallback. Vite's loadSiteEnv() injects it at build time;
 * if you reach this module without it, the build pipeline is broken and
 * silently defaulting would mean shipping the wrong tenant's identity.
 */

export type SiteType = "ecommerce" | "saas" | "museum" | "lodging" | "social_casino";

if (!import.meta.env.VITE_SITE_ID) {
  throw new Error(
    "VITE_SITE_ID was not injected at build time. Build with `SITE_ID=<tenant> npm run build`."
  );
}
export const SITE_ID = import.meta.env.VITE_SITE_ID as string;
export const SITE_TYPE: SiteType =
  (import.meta.env.VITE_SITE_TYPE as SiteType) || "ecommerce";
export const SITE_NAME = import.meta.env.VITE_SITE_NAME || "My Store";
export const SITE_SHORT_NAME = import.meta.env.VITE_SITE_SHORT_NAME || "Store";
export const SITE_URL = import.meta.env.VITE_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "");
export const SITE_TAGLINE = import.meta.env.VITE_SITE_TAGLINE || "";
/**
 * Build-time colour-scheme baseline (`theme.colorSchemeDefault` in the site
 * config). The live `theme_config.color_scheme_default` wins once settings
 * arrive; this keeps a dark-first tenant from flashing light before that.
 */
export const SITE_COLOR_SCHEME_DEFAULT: "light" | "dark" | "system" = (() => {
  const raw = import.meta.env.VITE_SITE_COLOR_SCHEME_DEFAULT as string | undefined;
  return raw === "dark" || raw === "system" ? raw : "light";
})();
/**
 * Default country for this tenant (ISO 3166-1 alpha-2, e.g. "US").
 * Used as a fallback in schema.org address output when the property row
 * doesn't carry an explicit country field.
 * Injected at build time from `sites/{id}/site.config.ts` via Vite.
 */
export const SITE_COUNTRY: string =
  (import.meta.env.VITE_SITE_COUNTRY as string | undefined) || "";

export const IS_SAAS = SITE_TYPE === "saas";
export const IS_MUSEUM = SITE_TYPE === "museum";
export const IS_LODGING = SITE_TYPE === "lodging";
export const IS_SOCIAL_CASINO = SITE_TYPE === "social_casino";

/**
 * Single-property lodging variant. True only for lodging tenants that set
 * `features.single_property: true` in site.config.ts (injected as
 * VITE_SINGLE_PROPERTY at build time). When on, the storefront collapses to a
 * single home: `/` renders that property (photo hero + live booking widget)
 * with the marketing sections below, `/properties` redirects to `/`, the
 * header/hero "Book Now" jumps straight to the booking widget, and the hero
 * image defaults to the property's main photo. Meaningless (always false)
 * outside lodging.
 */
export const IS_SINGLE_PROPERTY =
  IS_LODGING && import.meta.env.VITE_SINGLE_PROPERTY === "true";

/**
 * Whether this tenant has commerce features (cart/checkout/orders) active.
 * For ecommerce tenants this is always true. For museum tenants it follows
 * the `features.ecommerce` flag — off by default, on if the wizard's gift-shop
 * opt-in was checked. Read from VITE_FEATURE_ECOMMERCE injected at build time.
 * Lodging tenants use the booking flow (Properties/Reservations) instead of
 * the cart/checkout flow, so commerce is off — bookings have their own routes.
 *
 * Frontend uses this to gate /shop, /cart, /checkout, /orders routes and the
 * cart icon in the header. Backend has parallel guards on /api/products etc.
 */
export const IS_ECOMMERCE = (() => {
  if (IS_SAAS) return false;
  if (IS_LODGING) return false;
  if (IS_SOCIAL_CASINO) return false;
  if (!IS_MUSEUM) return true; // ecommerce site type → always commerce
  // Museum: opt-in via build-time feature flag
  return import.meta.env.VITE_FEATURE_ECOMMERCE === "true";
})();

/**
 * True only for rumio.world itself — the master platform that sells the
 * Rumio product. Other SaaS tenants that happen to be marketing their own
 * product (meal planners, CRMs, etc.) render tenant-specific marketing
 * content instead of the Rumio-branded hardcoded pages.
 */
export const IS_RUMIO = SITE_ID === "rumio";

/**
 * Lucide icon name used as this tenant's brand glyph in image placeholders
 * and empty states (e.g. product card with no image, search-no-results).
 * Picked by the wizard at site creation; falls back to "Sparkles" when the
 * tenant config doesn't supply one. The BrandSymbol component validates
 * against the curated catalog at sites/lucide-icon-catalog.ts.
 */
export const SITE_BRAND_SYMBOL =
  (import.meta.env.VITE_SITE_BRAND_SYMBOL as string | undefined) || "Sparkles";

/**
 * Tenant's default/source locale (BCP-47, e.g. "en", "es", "es-MX"). The bare
 * URL (no locale prefix) serves this locale. Injected by Vite from
 * `sites/{id}/site.config.ts` at build time.
 */
export const DEFAULT_LOCALE: string =
  (import.meta.env.VITE_SITE_DEFAULT_LOCALE as string) || "en";

/**
 * Locales this tenant serves. Always includes DEFAULT_LOCALE. Non-default
 * locales are reachable at `/{locale}/...`. Single-locale tenants get a
 * single-element array; the locale switcher hides itself when length === 1.
 */
export const SUPPORTED_LOCALES: string[] = (() => {
  const raw = (import.meta.env.VITE_SITE_SUPPORTED_LOCALES as string | undefined) || DEFAULT_LOCALE;
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
})();

/** Convenience flag — true when the tenant serves more than one locale. */
export const IS_MULTILINGUAL = SUPPORTED_LOCALES.length > 1;
